/**
 * Shared Keyfuels / eflex fuel-card upload helpers for Data Feed and Control Centre Fuel.
 */

import * as XLSX from 'xlsx';
import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from './apiClient';
import { isValidCalendarYmd } from './calendarYmd';

export const FUEL_TRANSACTION_ACCEPT =
  '.csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const FUEL_TRANSACTION_EXTENSIONS = ['.csv', '.xlsx', '.xls'] as const;

/** Timeout for declared / eflex ingest uploads (fuel, daily plan, tracker). */
export const DECLARED_INGEST_TIMEOUT_MS = 120_000;

/** @deprecated Use DECLARED_INGEST_TIMEOUT_MS */
export const FUEL_INGEST_TIMEOUT_MS = DECLARED_INGEST_TIMEOUT_MS;

export type EflexFuelRowPayload = {
  transactionId: string;
  vehicleRegistration: string;
  transactionDateTime: string;
  quantity: number;
  unitPrice?: number;
  totalPriceGbp?: number;
  companyProductName?: string;
  siteName?: string;
  siteTown?: string;
  network?: string;
  cardNumber?: string;
  odometerMiles?: number;
  transType?: string;
  authority?: 'observed' | 'declared';
  transportMode?: 'road' | 'rail' | 'maritime' | 'air' | 'intermodal';
};

export type EflexSkippedRow = {
  row: number;
  reason: string;
  reason_label: string;
  type?: string;
  product?: string;
  registration?: string;
  date?: string;
  quantity?: number;
  unit_price?: number;
  total_price_gbp?: number;
  candidate?: EflexFuelRowPayload;
};

export type EflexIngestResponse = {
  status?: string;
  job_id?: string;
  correlation_id?: string;
  raw_object_id?: string;
  message?: string;
  items_queued?: number;
  input_records?: number;
  skipped?: number;
  skipped_rows?: EflexSkippedRow[];
  skipped_rows_truncated?: boolean;
  week_start_date?: string;
  parse?: { warnings?: string[]; stats?: Record<string, number> };
  error?: string;
};

export function isFuelTransactionFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return FUEL_TRANSACTION_EXTENSIONS.some((ext) => name.endsWith(ext));
}

/** First worksheet → CSV text for eflex parseEflexCsv. */
export async function excelFileToCsvText(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  // cellDates + dateNF forces date-typed cells to ISO YYYY-MM-DD regardless of
  // the sheet's stored number format (which is often US m/d/yy or a raw serial).
  // Without this, sheet_to_csv emits whatever the cell format says and
  // normaliseUkOrIsoDate then reads "9/7/26" as 9 July, or drops bare serials.
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) {
    throw new Error('Excel workbook has no sheets.');
  }
  const sheet = workbook.Sheets[sheetName];
  return XLSX.utils.sheet_to_csv(sheet, { dateNF: 'yyyy-mm-dd' });
}

export async function fileToCsvTextForFuel(file: File): Promise<string> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.csv')) {
    return file.text();
  }
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
    return excelFileToCsvText(file);
  }
  throw new Error('Fuel Transactions expects .csv, .xlsx, or .xls.');
}

/**
 * Best-effort min/max calendar dates from a Keyfuels export CSV (Date column).
 * Returns null when no parseable dates are found.
 */
export function detectFuelCsvPeriod(csv: string): { start: string; end: string } | null {
  const lines = csv.split(/\r\n|\r|\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) return null;

  const header = splitCsvLine(lines[0]).map((h) => h.replace(/^"|"$/g, '').trim().toLowerCase());
  const dateIdx = header.findIndex((h) => h === 'date');
  if (dateIdx < 0) return null;

  const ymds: string[] = [];
  for (let i = 1; i < lines.length; i += 1) {
    const cols = splitCsvLine(lines[i]);
    const raw = (cols[dateIdx] ?? '').trim();
    const ymd = normaliseUkOrIsoDate(raw);
    if (ymd) ymds.push(ymd);
  }
  if (ymds.length === 0) return null;
  ymds.sort();
  return { start: ymds[0], end: ymds[ymds.length - 1] };
}

export function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === ',' && !inQuotes) {
      out.push(cur);
      cur = '';
      continue;
    }
    cur += ch;
  }
  out.push(cur);
  return out;
}

/** Accepts DD/MM/YYYY, DD/MM/YY, or YYYY-MM-DD (real calendar dates only). */
function normaliseUkOrIsoDate(raw: string): string | null {
  const value = raw.trim();
  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    const ymd = `${iso[1]}-${iso[2]}-${iso[3]}`;
    return isValidCalendarYmd(ymd) ? ymd : null;
  }
  const uk = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (!uk) return null;
  let d = Number(uk[1]);
  let m = Number(uk[2]);
  let y = Number(uk[3]);
  if (y < 100) y += 2000;
  // Keyfuels exports are UK DD/MM, but an .xlsx round-tripped through a US
  // locale can yield MM/DD — only swap when the first field cannot be a month.
  if (m > 12 && d <= 12) {
    [d, m] = [m, d];
  }
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  const ymd = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  return isValidCalendarYmd(ymd) ? ymd : null;
}

export async function uploadEflexFuelTransactions(
  file: File,
): Promise<{ response: Response; json: EflexIngestResponse; csv: string }> {
  const csv = await fileToCsvTextForFuel(file);
  const response = await authenticatedFetch(
    API_ENDPOINTS.eflexIngest.transactions(),
    { method: 'POST', body: JSON.stringify({ csv }) },
    DECLARED_INGEST_TIMEOUT_MS,
  );
  const json = (await response.json().catch(() => ({}))) as EflexIngestResponse;
  return { response, json, csv };
}

/** Queue already-normalised rows (e.g. user-accepted skips from Control Centre). */
export async function uploadEflexFuelRows(
  rows: EflexFuelRowPayload[],
): Promise<{ response: Response; json: EflexIngestResponse }> {
  // Accept overrides are user-declared; transportMode must come from the candidate
  // (parser stamps EFLEX_SOURCE_TRANSPORT_MODE) — do not invent 'road' here.
  const stamped = rows.map((r) => ({
    ...r,
    authority: r.authority ?? 'declared',
  }));
  const response = await authenticatedFetch(
    API_ENDPOINTS.eflexIngest.transactions(),
    { method: 'POST', body: JSON.stringify({ rows: stamped }) },
    DECLARED_INGEST_TIMEOUT_MS,
  );
  const json = (await response.json().catch(() => ({}))) as EflexIngestResponse;
  return { response, json };
}

export function formatEflexUploadMessage(
  filename: string,
  json: EflexIngestResponse,
): { kind: 'empty' | 'queued'; text: string } {
  if (json.status === 'empty') {
    return {
      kind: 'empty',
      text:
        `Parsed “${filename}” but found no rows to queue`
        + (json.parse?.stats
          ? ` (${Object.entries(json.parse.stats).map(([k, v]) => `${k}: ${v}`).join(', ')})`
          : '')
        + '.',
    };
  }

  const headlineParts = [
    `Queued “${filename}” for fuel transactions (eflex)`,
    json.week_start_date ? `week starting ${json.week_start_date}` : null,
  ].filter(Boolean);

  return {
    kind: 'queued',
    text: `${headlineParts.join(' · ')}.`,
  };
}
